/**
 * @fileoverview The page behind a share link. No account needed.
 *
 * It wears the garden page's look (gardenPageSections, itemPageSections): Grow's
 * mark and a Share button, the plant's one name (the backend's names,
 * services/grow/plantNaming: "Cherokee Purple Tomato") in the page's serif
 * with the plant's stage as a chip, the gardener's words in a card, and at
 * the foot the green "Grow
 * your own like Amy" card with "Start your garden free". It shows the plant,
 * the photographs and words the gardener chose when they made the link. A
 * link made before the pictures were kept has its photographs in the garden
 * page's photo scroller, each with the day it was taken; one with a composed
 * picture shows that picture (FrameStrip) under the name. A
 * season review's link ("Your 2026 season") shows its picture, headed with
 * its year, and the gardener's name only while that season's switch is on;
 * one made while the season ran ("Your season so far") says "So far, as of"
 * the day it was made, in the visitor's language.
 * Choosing those photographs was the consent for them. The gardener's name
 * appears only while the plant's public switch is on: that switch is the
 * consent for the name, and for the plant being listed on its bed's page.
 *
 * "Grown by" links the gardener's name to their garden page, at its
 * readable address (/@amy) when the backend gives one, else /g/<code>.
 *
 * The code follows a visitor into signup only if they choose to make an
 * account. Nothing else is planted on them.
 *
 * Where: the route /s/:code (App.tsx), public, outside the Layout.
 * API: GET /shares/:code (getShare; ?count=0 once counted).
 * Settings: VITE_API_URL (where photo paths point); the referral keys in
 * localStorage (REFERRAL_KEY, REFERRAL_AT_KEY, REFERRAL_NAME_KEY).
 * Words (en/es): share.page.* (missing, unavailable, takenDown, grownBy,
 * plain, growingHere, inviteTitle), gardenPage.page.* (latestFrom, cardPhotos,
 * itemShareText, inviteTitleOf, inviteBodyNew, inviteStart),
 * seasonReview.page.title, seasonReview.page.soFar, sitewide.loading.
 * A link that could not be read on our side (a 500, no connection; D31,
 * lib/serverFailure) says the standard sentence, reported to Sentry, with the
 * support address as a link, not "There is nothing at this link."; a 410
 * stays share.page.unavailable.
 * Watch out: opening a share always takes the sign-up credit, even over a
 * garden opened earlier; a garden page only takes it when no share has lately.
 *
 * @module SharePage
 */

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getShare, REFERRAL_AT_KEY, REFERRAL_KEY, REFERRAL_NAME_KEY, shareUrlFor, type PublicShare } from '@/shared/api/shares';
import { gardenPathFor, type GardenPhoto } from '@/shared/api/gardens';
import { SocialIcon } from '@/shared/components/SocialIcon';
import { DateTime } from '@/shared/utils/dateUtils';
import Spinner from '@/shared/components/ui/Spinner';
import { FrameStrip } from './FrameStrip';
import { GardenFrame, GardenInvite } from './gardenPageParts';
import { GARDEN_COLUMN_CLASS, SECTION_HEADING_CLASS, WordsCard } from './gardenPageSections';
import { ItemColumn, ItemHeading, ItemHero } from './itemPageSections';
import { datePartsOf, shortDay } from './lib/gardenPage';
import { isUnavailable } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';

/** Where the API is, as the app is built to reach it. */
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * The photo endpoints are relative to the API; this makes them absolute for an <img>.
 *
 * @param path - As the share sends it, "/api/…", or a full address.
 * @returns An address an <img> can load.
 */
function photoSrc(path: string): string {
  return path.startsWith('/api/') ? `${API}${path.slice(4)}` : path;
}

/** The page at /s/:code: the pictures the gardener composed, their words, and an invitation. */
export const SharePage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const [share, setShare] = React.useState<PublicShare | null>(null);
  const [missing, setMissing] = React.useState(false);
  /** The gardener behind the link is suspended: the page says only that it is unavailable. */
  const [unavailable, setUnavailable] = React.useState(false);
  /** What the read threw when it failed on our side (a 500, no connection); null otherwise. */
  const [ours, setOurs] = React.useState<unknown>(null);

  React.useEffect(() => {
    let cancelled = false;
    setShare(null);
    setMissing(false);
    setUnavailable(false);
    setOurs(null);
    getShare(code)
      .then((data) => { if (!cancelled) setShare(data); })
      .catch((error) => {
        if (cancelled) return;
        setMissing(true);
        setUnavailable(isUnavailable(error));
        setOurs(isServerFailure(error) ? error : null);
      });
    return () => { cancelled = true; };
  }, [code]);

  // The code rides along to signup, so a visitor who makes an account is
  // counted against the share that brought them.
  React.useEffect(() => {
    if (!code) return;
    try {
      window.localStorage.setItem(REFERRAL_KEY, code);
      // When, sent with sign-up so the server can tell an old one (A73).
      window.localStorage.setItem(REFERRAL_AT_KEY, String(Date.now()));
    } catch { /* private mode */ }
  }, [code]);
  // The gardener's name goes with it only while their plant is public.
  React.useEffect(() => {
    if (!share) return;
    try {
      if (share.gardener?.name) window.localStorage.setItem(REFERRAL_NAME_KEY, share.gardener.name);
      else window.localStorage.removeItem(REFERRAL_NAME_KEY);
    } catch { /* private mode */ }
  }, [share]);

  /** What the page is of: the plant's one name (the backend's names), the bed's name, or the season ("2026 in the garden"). */
  const heading = share?.season
    ? t('seasonReview.page.title', { year: share.season.year })
    : (share?.plant ? share.plant.names.full : share?.location?.name) ?? '';
  /** A "so far" season link's day, "October 5, 2026", in the visitor's language; null otherwise. */
  const asOf = share?.season?.soFar && share.season.asOf
    ? DateTime.fromISO(share.season.asOf).setLocale(language).toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  /** The photographs of a link made before its pictures were kept, for the scroller; a link with a picture shows the picture. */
  const photos: GardenPhoto[] = share && !share.frame
    ? share.photos.map((photo) => ({ src: photo.url, ...(photo.large ? { large: photo.large } : {}), date: photo.dateTaken }))
    : [];
  /** The stage of the newest of those photographs, as text. */
  const newestStage = share
    ? [...share.photos].sort((a, b) => (b.dateTaken ?? '').localeCompare(a.dateTaken ?? '')).find((photo) => photo.stage)?.stage ?? null
    : null;
  const gardenerName = share?.gardener?.name ?? null;
  /** The line under the heading: the season's day or the bed's description; a plant's one name says it all, so nothing under it. */
  const subtitle = asOf ? t('seasonReview.page.soFar', { date: asOf }) : (share?.plant ? null : share?.location?.description) || null;

  return (
    <GardenFrame bare>
      {!share && (
        <div className={`${GARDEN_COLUMN_CLASS} max-w-lg py-10`}>
          <p className="mb-8 flex items-center gap-2 font-grow text-2xl font-bold text-primary">
            <Sprout className="h-6 w-6" aria-hidden="true" />
            {t('landing.brand')}
            <span className="self-end pb-0.5 text-sm font-semibold text-muted-foreground">{t('landing.brandBy')}</span>
          </p>
          {missing && (ours
            ? <ServerFailureNote error={ours} where="Share page" className="text-base text-muted-foreground" />
            : <p className="text-muted-foreground">{t(unavailable ? 'share.page.unavailable' : 'share.page.missing')}</p>)}
          {!missing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
          )}
        </div>
      )}

      {share && (
        <>
          <ItemHero
            photos={photos}
            label={t('gardenPage.page.cardPhotos', { name: heading })}
            link={shareUrlFor(code)}
            title={heading}
          />
          <ItemColumn narrow>
            {share.revoked && (
              <p className="rounded-2xl border px-3 py-2 text-sm text-muted-foreground">{t('share.page.takenDown')}</p>
            )}

            <div>
              <ItemHeading title={heading} line={subtitle} stageText={newestStage} />

              {share.gardener && (
                <p className="mt-4 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{t('share.page.grownBy')}</span>
                  {share.gardener.place && <SocialIcon name={share.gardener.place} size={14} labelled />}
                  {share.gardener.garden ? (
                    <Link to={gardenPathFor(share.gardener.garden, share.gardener.slug)} className="font-medium underline decoration-1 underline-offset-4 hover:decoration-2">
                      {share.gardener.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{share.gardener.name}</span>
                  )}
                </p>
              )}
            </div>

            {share.words && (
              <WordsCard
                words={share.words}
                meta={[shortDay(share.createdAt, datePartsOf(share.dates)), gardenerName ? t('gardenPage.page.latestFrom', { name: gardenerName }) : null].filter(Boolean).join(' · ') || null}
              />
            )}

            {/* The picture as the gardener composed it in the share sheet, and
                that alone: the card, the peek, the before-and-after, whichever
                they chose. The photographs it was made from are listed only
                for a link made before the sheet kept its picture (the photo
                scroller at the top). */}
            {share.frame ? (
              <FrameStrip
                frames={(share.frames.length ? share.frames : [share.frame]).map(photoSrc)}
                alt={heading}
              />
            ) : share.photos.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('share.page.plain')}</p>
            )}

            {/* A location's page: the plants growing there whose own page is on, each a way onward. */}
            {share.open && share.location && share.location.plants.length > 0 && (
              <section>
                <h2 className={SECTION_HEADING_CLASS}>{t('share.page.growingHere')}</h2>
                <ul className="mt-3 divide-y rounded-2xl border bg-card">
                  {share.location.plants.map((plant, index) => (
                    <li key={`${plant.variety}-${index}`} className="px-4 py-3 text-sm">
                      {/* Each by its one name (the backend's names); its kind is not said beside it. */}
                      {plant.code ? (
                        <Link to={`/s/${plant.code}`} className="font-medium hover:underline">{plant.names.full}</Link>
                      ) : (
                        <span className="font-medium">{plant.names.full}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* The invitation. The whole reason the page exists; this link's code is the sign-up's credit. */}
            <div className="mx-auto max-w-xl pt-4">
              <GardenInvite code={code} name={gardenerName} card />
            </div>
          </ItemColumn>
        </>
      )}
    </GardenFrame>
  );
};

export default SharePage;
